import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Loader2, Link2, Copy, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import { shareViaWhatsApp, WhatsAppIcon } from '../../lib/whatsapp';

/**
 * Sales-only: generate a Razorpay payment link for a miscellaneous charge.
 * The Description field becomes the invoice line item.
 */
export const MiscPaymentLinkModal = ({ open, onClose, api, venues = [], onCreated }) => {
  const [form, setForm] = useState({
    guest_name: '', guest_email: '', guest_phone: '',
    guest_company: '', guest_gstin: '',
    description: '', amount: '', discount_percent: 0,
    gst_applicable: true, gst_rate: 18,
    venue_id: '', notes: '',
    is_partial_payment: false, partial_amount: '', balance_due_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setForm({
        guest_name: '', guest_email: '', guest_phone: '',
        guest_company: '', guest_gstin: '',
        description: '', amount: '', discount_percent: 0,
        gst_applicable: true, gst_rate: 18,
        venue_id: '', notes: '',
        is_partial_payment: false, partial_amount: '', balance_due_date: '',
      });
    }
  }, [open]);

  const preview = useMemo(() => {
    const base = parseFloat(form.amount) || 0;
    const discountPct = Math.max(0, Math.min(100, parseFloat(form.discount_percent) || 0));
    const discountAmt = +(base * discountPct / 100).toFixed(2);
    const discountedBase = +(base - discountAmt).toFixed(2);
    const gst = form.gst_applicable ? +(discountedBase * form.gst_rate / 100).toFixed(2) : 0;
    const total = +(discountedBase + gst).toFixed(2);
    const partial = form.is_partial_payment ? Math.min(parseFloat(form.partial_amount) || 0, total) : 0;
    const balance = form.is_partial_payment ? Math.max(0, +(total - partial).toFixed(2)) : 0;
    return { base, discountPct, discountAmt, discountedBase, gst, total, partial, balance };
  }, [form.amount, form.discount_percent, form.gst_applicable, form.gst_rate, form.is_partial_payment, form.partial_amount]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.guest_name.trim() || !form.guest_email.trim()) return toast.error('Client name and email are required');
    if (!form.description.trim() || form.description.trim().length < 3) return toast.error('Description is required');
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Amount must be greater than 0');
    if (form.is_partial_payment) {
      const partial = parseFloat(form.partial_amount) || 0;
      if (partial <= 0) return toast.error('Partial payment amount must be greater than 0');
      if (partial >= preview.total) return toast.error(`Partial amount must be less than the total (₹${preview.total.toLocaleString('en-IN')})`);
      if (!form.balance_due_date) return toast.error('Balance due date is required');
    }

    setSaving(true);
    try {
      const res = await api.post('/bookings/misc-payment-link', {
        guest_name: form.guest_name.trim(),
        guest_email: form.guest_email.trim(),
        guest_phone: form.guest_phone.trim() || undefined,
        guest_company: form.guest_company.trim() || undefined,
        guest_gstin: form.guest_gstin.trim() || undefined,
        description: form.description.trim(),
        amount: parseFloat(form.amount),
        discount_percent: parseFloat(form.discount_percent) || 0,
        gst_applicable: form.gst_applicable,
        gst_rate: parseInt(form.gst_rate) || 18,
        venue_id: form.venue_id || undefined,
        notes: form.notes.trim() || undefined,
        is_partial_payment: form.is_partial_payment,
        partial_amount: form.is_partial_payment ? parseFloat(form.partial_amount) : undefined,
        balance_due_date: form.is_partial_payment ? form.balance_due_date : undefined,
      });
      setResult(res.data);
      toast.success('Payment link generated');
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="misc-payment-link-modal">
        <DialogHeader>
          <DialogTitle>Custom Payment Link</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            For miscellaneous charges outside the normal booking flow. Generates a Razorpay link + Zoho invoice using the description as the line item.
          </p>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 py-2">
            <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 space-y-3">
              <div>
                <p className="text-sm font-semibold text-emerald-700">Payment link ready</p>
                <p className="text-xs text-muted-foreground">
                  Total: <b>₹{result.total_amount?.toLocaleString('en-IN')}</b>
                  {result.zoho_invoice_number ? ` · Invoice: ${result.zoho_invoice_number}` : ''}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wider">
                  {result.is_partial_payment ? `Partial payment link · ₹${result.partial_amount?.toLocaleString('en-IN')}` : 'Payment link'}
                </p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={result.payment_link} className="text-xs font-mono" data-testid="misc-payment-link-url" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(result.payment_link); toast.success('Copied'); }} title="Copy">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareViaWhatsApp(
                    result.is_partial_payment
                      ? `Hi ${result.guest_name}, here's your Clayworks partial payment link for ₹${result.partial_amount?.toLocaleString('en-IN')} (balance ₹${result.balance_amount?.toLocaleString('en-IN')} due by ${result.balance_due_date}): ${result.payment_link}`
                      : `Hi ${result.guest_name}, here's your Clayworks payment link for ₹${result.total_amount?.toLocaleString('en-IN')}: ${result.payment_link}`,
                    result.guest_phone
                  )} className="text-green-600" title="Share on WhatsApp">
                    <WhatsAppIcon className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={result.payment_link} target="_blank" rel="noopener noreferrer" title="Open"><Link2 className="w-3.5 h-3.5" /></a>
                  </Button>
                </div>
              </div>

              {result.is_partial_payment && result.balance_payment_link && (
                <div className="space-y-1 pt-2 border-t border-emerald-500/20">
                  <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider">
                    Balance link · ₹{result.balance_amount?.toLocaleString('en-IN')} · due {result.balance_due_date}
                  </p>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={result.balance_payment_link} className="text-xs font-mono" data-testid="misc-balance-link-url" />
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(result.balance_payment_link); toast.success('Balance link copied'); }} title="Copy">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => shareViaWhatsApp(
                      `Hi ${result.guest_name}, here's the balance payment link for ₹${result.balance_amount?.toLocaleString('en-IN')} (due by ${result.balance_due_date}): ${result.balance_payment_link}`,
                      result.guest_phone
                    )} className="text-green-600" title="Share balance on WhatsApp">
                      <WhatsAppIcon className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <a href={result.balance_payment_link} target="_blank" rel="noopener noreferrer" title="Open"><Link2 className="w-3.5 h-3.5" /></a>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client name *</Label>
                <Input value={form.guest_name} onChange={(e) => update('guest_name', e.target.value)} data-testid="misc-guest-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.guest_email} onChange={(e) => update('guest_email', e.target.value)} data-testid="misc-guest-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.guest_phone} onChange={(e) => update('guest_phone', e.target.value)} placeholder="+91…" data-testid="misc-guest-phone" />
              </div>
              <div>
                <Label>Company</Label>
                <Input value={form.guest_company} onChange={(e) => update('guest_company', e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>GSTIN (optional)</Label>
                <Input value={form.guest_gstin} onChange={(e) => update('guest_gstin', e.target.value.toUpperCase())} placeholder="e.g. 29ABCDE1234F1Z5" />
              </div>
              <div className="col-span-2">
                <Label>Description * <span className="text-muted-foreground text-xs">(appears as the invoice line item)</span></Label>
                <Textarea rows={2} value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="e.g. Extended day pass, custom event booking, AV equipment rental…" data-testid="misc-description" />
              </div>
              <div>
                <Label>Amount (before GST) *</Label>
                <div className="relative">
                  <IndianRupee className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input type="number" step="0.01" min={1} value={form.amount} onChange={(e) => update('amount', e.target.value)} className="pl-7" data-testid="misc-amount" />
                </div>
              </div>
              <div>
                <Label>GST</Label>
                <Select value={form.gst_applicable ? String(form.gst_rate) : 'none'} onValueChange={(v) => {
                  if (v === 'none') { update('gst_applicable', false); }
                  else { update('gst_applicable', true); update('gst_rate', parseInt(v)); }
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="18">18%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="none">Not applicable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Discount (%) <span className="text-muted-foreground text-xs">(optional, applied before GST)</span></Label>
                <Input
                  type="number" min={0} max={100} step="0.1" placeholder="0"
                  value={form.discount_percent || ''}
                  onChange={(e) => { const v = parseFloat(e.target.value) || 0; update('discount_percent', Math.max(0, Math.min(100, v))); }}
                  data-testid="misc-discount"
                />
              </div>
              <div className="col-span-2">
                <Label>Venue (optional, for invoice template)</Label>
                <Select value={form.venue_id} onValueChange={(v) => update('venue_id', v)}>
                  <SelectTrigger><SelectValue placeholder="Generic template" /></SelectTrigger>
                  <SelectContent>
                    {venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name} · {v.city}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Internal notes</Label>
                <Textarea rows={1} value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Anything for the team's reference (not shown to client)" />
              </div>
            </div>

            {preview.base > 0 && (
              <div className="rounded-md border border-border p-3 space-y-2" data-testid="misc-partial-block">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_partial_payment}
                    onChange={(e) => update('is_partial_payment', e.target.checked)}
                    className="w-4 h-4 rounded border-border"
                    data-testid="misc-partial-toggle"
                  />
                  <span className="text-sm font-medium">Split into partial + balance payment</span>
                </label>
                {form.is_partial_payment && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                      <Label className="text-xs">Amount to collect now *</Label>
                      <div className="relative">
                        <IndianRupee className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                        <Input
                          type="number" step="0.01" min={1} max={preview.total - 1}
                          value={form.partial_amount}
                          onChange={(e) => update('partial_amount', e.target.value)}
                          placeholder={`Max ₹${(preview.total - 1).toFixed(2)}`}
                          className="pl-7" data-testid="misc-partial-amount"
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">Balance: <b>₹{preview.balance.toLocaleString('en-IN')}</b></p>
                    </div>
                    <div>
                      <Label className="text-xs">Balance due date *</Label>
                      <Input
                        type="date"
                        min={new Date().toISOString().slice(0, 10)}
                        value={form.balance_due_date}
                        onChange={(e) => update('balance_due_date', e.target.value)}
                        data-testid="misc-balance-due-date"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {preview.base > 0 && (
              <div className="p-3 rounded-md bg-muted/40 text-xs space-y-1" data-testid="misc-preview">
                <div className="flex justify-between"><span className="text-muted-foreground">Base amount</span><span>₹{preview.base.toLocaleString('en-IN')}</span></div>
                {preview.discountAmt > 0 && (
                  <div className="flex justify-between text-amber-700"><span>Discount ({preview.discountPct}%)</span><span>- ₹{preview.discountAmt.toLocaleString('en-IN')}</span></div>
                )}
                {form.gst_applicable && (
                  <div className="flex justify-between"><span className="text-muted-foreground">GST ({form.gst_rate}%)</span><span>₹{preview.gst.toLocaleString('en-IN')}</span></div>
                )}
                <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Total payable</span><span>₹{preview.total.toLocaleString('en-IN')}</span></div>
                {form.is_partial_payment && preview.partial > 0 && (
                  <>
                    <div className="flex justify-between text-amber-700 font-medium"><span>Pay now (partial)</span><span>₹{preview.partial.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>Balance{form.balance_due_date ? ` (due ${form.balance_due_date})` : ''}</span><span>₹{preview.balance.toLocaleString('en-IN')}</span></div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
          {!result && (
            <Button onClick={submit} disabled={saving} data-testid="misc-generate-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              Generate Link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
